
// Pure, Level-1-testable binary writers for the ESRI Shapefile Technical
// Description's Z-enabled shape types (PointZ=11, PolyLineZ=13, PolygonZ=15)
// - per the ESRI whitepaper (https://www.esri.com/content/dam/esrisites/
// sitecore-archive/Files/Pdfs/library/whitepapers/pdfs/shapefile.pdf).
//
// Task section 9/11 audit finding: no current npm Shapefile-writing library
// (checked @mapbox/shp-write 0.4.3, the most-used option) supports the Z
// shape types at all - its own points.js/poly.js only ever emit the 2D
// POINT/POLYLINE/POLYGON codes (1/3/5) and silently drop any Z coordinate
// present in the input. Task section 20 requires 3D coordinates to be
// preserved, so this file implements the Z-variant record layout directly
// against the stable, public ESRI spec - the DBF attribute table (the
// genuinely complex, error-prone part of a shapefile) still comes entirely
// from the mature `dbf` npm package, and the ZIP container from `jszip` -
// see ../ShapefileFeatureExporter.js. This is a bounded, spec-driven
// extension of the geometry envelope only, not a from-scratch
// reimplementation of the format (task section 9's own instruction).
//
// Deliberately single-part only (no MultiPoint/MultiLineString/MultiPolygon,
// no polygon holes/inner rings) - confirmed elsewhere in this codebase
// (geometry/topologyQa.js's own audit comment: "this codebase has no hole/
// multi-ring polygon support anywhere") that every PointView Point/
// LineString/Polygon feature is already exactly one part - a generic
// multi-part writer would be unused complexity for data this project can
// never actually produce.

const SHAPE_TYPE = {POINTZ: 11, POLYLINEZ: 13, POLYGONZ: 15};

function blankExtent(){
	return {xmin: Infinity, ymin: Infinity, xmax: -Infinity, ymax: -Infinity, zmin: Infinity, zmax: -Infinity};
}

function enlarge(extent, x, y, z){
	if(x < extent.xmin) extent.xmin = x;
	if(x > extent.xmax) extent.xmax = x;
	if(y < extent.ymin) extent.ymin = y;
	if(y > extent.ymax) extent.ymax = y;
	if(z < extent.zmin) extent.zmin = z;
	if(z > extent.zmax) extent.zmax = z;
	return extent;
}

// Shared 100-byte main file header (identical structure for .shp and .shx,
// differing only in total file length and, for .shp, the shape-specific
// bounding box) - see ESRI spec section "Main File Header".
function writeMainHeader(view, fileLength16BitWords, shapeType, extent){
	view.setInt32(0, 9994); // file code, big-endian
	view.setInt32(24, fileLength16BitWords); // big-endian
	view.setInt32(28, 1000, true); // version, little-endian
	view.setInt32(32, shapeType, true);
	view.setFloat64(36, extent.xmin, true);
	view.setFloat64(44, extent.ymin, true);
	view.setFloat64(52, extent.xmax, true);
	view.setFloat64(60, extent.ymax, true);
	view.setFloat64(68, extent.zmin, true);
	view.setFloat64(76, extent.zmax, true);
	// Mmin/Mmax (84/92) intentionally left 0 - no M (measure) values in any
	// PointView feature; M is fully optional in the on-disk format.
}

// `points`: array of [x, y, z] triples (single Point feature per record - a
// Point never has multiple parts). Returns {shp, shx} DataViews.
export function writePointZRecords(points){
	const RECORD_CONTENT_LENGTH = 28; // type(4) + X(8) + Y(8) + Z(8), no M
	const extent = points.reduce((e, [x, y, z]) => enlarge(e, x, y, z), blankExtent());

	const shpBuffer = new ArrayBuffer(100 + points.length * (8 + RECORD_CONTENT_LENGTH));
	const shpView = new DataView(shpBuffer);
	const shxBuffer = new ArrayBuffer(100 + points.length * 8);
	const shxView = new DataView(shxBuffer);

	writeMainHeader(shpView, shpBuffer.byteLength / 2, SHAPE_TYPE.POINTZ, points.length ? extent : blankZeroExtent());
	writeMainHeader(shxView, shxBuffer.byteLength / 2, SHAPE_TYPE.POINTZ, points.length ? extent : blankZeroExtent());

	let shpOffset = 100, shxOffset = 100;
	points.forEach(([x, y, z], i) => {
		shpView.setInt32(shpOffset, i + 1); // record number, 1-based, big-endian
		shpView.setInt32(shpOffset + 4, RECORD_CONTENT_LENGTH / 2); // big-endian
		shpView.setInt32(shpOffset + 8, SHAPE_TYPE.POINTZ, true);
		shpView.setFloat64(shpOffset + 12, x, true);
		shpView.setFloat64(shpOffset + 20, y, true);
		shpView.setFloat64(shpOffset + 28, z || 0, true);

		shxView.setInt32(shxOffset, shpOffset / 2); // big-endian, offset in words
		shxView.setInt32(shxOffset + 4, RECORD_CONTENT_LENGTH / 2);

		shpOffset += 8 + RECORD_CONTENT_LENGTH;
		shxOffset += 8;
	});

	return {shp: shpView, shx: shxView};
}

function blankZeroExtent(){
	return {xmin: 0, ymin: 0, xmax: 0, ymax: 0, zmin: 0, zmax: 0};
}

// The ring a PolygonZ record must hold, per the ESRI spec: closed (the last
// vertex repeats the first) and clockwise, since the order of the vertices is
// what marks the inside of the polygon. `coords`: [x, y, z] triples, closed or
// open and in either direction. Each vertex keeps its own Z.
export function shapefilePolygonRing(coords){
	if(coords.length < 2) return coords;
	const [x0, y0, z0] = coords[0];
	const [xn, yn, zn] = coords[coords.length - 1];
	const closed = x0 === xn && y0 === yn && (z0 || 0) === (zn || 0);
	const open = closed ? coords.slice(0, -1) : coords;

	let doubledArea = 0;
	for(let i = 0; i < open.length; i++){
		const [ax, ay] = open[i], [bx, by] = open[(i + 1) % open.length];
		doubledArea += (ax - x0) * (by - y0) - (bx - x0) * (ay - y0);
	}
	const clockwise = doubledArea > 0 ? [...open].reverse() : open;
	return [...clockwise, clockwise[0]];
}

// `features`: array of vertex arrays (each an array of [x,y,z] triples) - one
// per LineString/Polygon feature (each is its own single-part record).
// A Polygon's ring must already be in the form the format requires, see
// shapefilePolygonRing() below.
export function writePolyZRecords(features, shapeType){
	const overallExtent = features.reduce((e, verts) => verts.reduce((e2, [x, y, z]) => enlarge(e2, x, y, z), e), blankExtent());

	let totalContentLength = 0;
	const perFeature = features.map((verts) => {
		const n = verts.length;
		// type(4) + box(32) + numParts(4) + numPoints(4) + parts(4*1) + points(16*n) + zmin(8) + zmax(8) + zarray(8*n)
		const contentLength = 4 + 32 + 4 + 4 + 4 + (16 * n) + 8 + 8 + (8 * n);
		totalContentLength += contentLength;
		return contentLength;
	});

	const shpBuffer = new ArrayBuffer(100 + features.reduce((sum, _, i) => sum + 8 + perFeature[i], 0));
	const shpView = new DataView(shpBuffer);
	const shxBuffer = new ArrayBuffer(100 + features.length * 8);
	const shxView = new DataView(shxBuffer);

	writeMainHeader(shpView, shpBuffer.byteLength / 2, shapeType, features.length ? overallExtent : blankZeroExtent());
	writeMainHeader(shxView, shxBuffer.byteLength / 2, shapeType, features.length ? overallExtent : blankZeroExtent());

	let shpOffset = 100, shxOffset = 100;
	features.forEach((verts, i) => {
		const n = verts.length;
		const contentLength = perFeature[i];
		const featureExtent = verts.reduce((e, [x, y, z]) => enlarge(e, x, y, z), blankExtent());

		shpView.setInt32(shpOffset, i + 1);
		shpView.setInt32(shpOffset + 4, contentLength / 2);

		let p = shpOffset + 8;
		shpView.setInt32(p, shapeType, true); p += 4;
		shpView.setFloat64(p, featureExtent.xmin, true); p += 8;
		shpView.setFloat64(p, featureExtent.ymin, true); p += 8;
		shpView.setFloat64(p, featureExtent.xmax, true); p += 8;
		shpView.setFloat64(p, featureExtent.ymax, true); p += 8;
		shpView.setInt32(p, 1, true); p += 4; // NumParts - always 1 (single-part only)
		shpView.setInt32(p, n, true); p += 4; // NumPoints
		shpView.setInt32(p, 0, true); p += 4; // Parts[0] = 0 (the one part starts at point 0)
		for(const [x, y] of verts){
			shpView.setFloat64(p, x, true); p += 8;
			shpView.setFloat64(p, y, true); p += 8;
		}
		shpView.setFloat64(p, featureExtent.zmin, true); p += 8;
		shpView.setFloat64(p, featureExtent.zmax, true); p += 8;
		for(const [, , z] of verts){
			shpView.setFloat64(p, z || 0, true); p += 8;
		}

		shxView.setInt32(shxOffset, shpOffset / 2);
		shxView.setInt32(shxOffset + 4, contentLength / 2);

		shpOffset += 8 + contentLength;
		shxOffset += 8;
	});

	return {shp: shpView, shx: shxView};
}

export {SHAPE_TYPE};
